import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, ChevronRight, Clock, Download, Smartphone, XCircle } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type * as S from "@shipyard/shared";
import { PlatformIcon } from "@/components/common";
import { Logo } from "@/components/layout";
import { Button, Card, EmptyState, Spinner } from "@/components/ui";
import { LanguageMenu } from "@/components/language-menu";
import { usePublicInstall, usePublicPage } from "@/lib/queries";
import { cn, formatDate } from "@/lib/utils";

const DEVICE_KEY = "shipyard.device";

type Visitor = "ios" | "android" | "desktop";

function detectVisitor(): Visitor {
  const ua = navigator.userAgent;
  // iPadOS reports a Mac user agent: tell it apart with touch support.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

/** Device id returned by the enrollment redirect (`?device=`), remembered in this browser. */
function useDeviceId(): string | null {
  const [device] = useState<string | null>(() => {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get("device");
    try {
      if (fromUrl) {
        localStorage.setItem(DEVICE_KEY, fromUrl);
        url.searchParams.delete("device");
        window.history.replaceState(null, "", url.toString());
        return fromUrl;
      }
      return localStorage.getItem(DEVICE_KEY);
    } catch {
      return fromUrl;
    }
  });
  return device;
}

/** Brand color of a white-label page: overrides the brand palette of this subtree. */
function brandStyle(color: string | null | undefined): CSSProperties | undefined {
  if (!color) return undefined;
  return {
    "--color-brand-50": `color-mix(in srgb, ${color} 8%, white)`,
    "--color-brand-100": `color-mix(in srgb, ${color} 16%, white)`,
    "--color-brand-500": color,
    "--color-brand-600": color,
    "--color-brand-700": `color-mix(in srgb, ${color} 85%, black)`,
  } as CSSProperties;
}

function BrandLogo({ branding }: { branding: S.PublicBranding | null | undefined }) {
  if (!branding?.logoUrl && !branding?.name) return <Logo />;
  return (
    <div className="flex items-center gap-2 font-semibold tracking-tight">
      {branding.logoUrl && <img src={branding.logoUrl} alt={branding.name ?? ""} className="h-7 max-w-40 object-contain" />}
      {branding.name && <span>{branding.name}</span>}
    </div>
  );
}

function PublicFooter({ branding }: { branding: S.PublicBranding | null | undefined }) {
  const { t } = useTranslation();
  const poweredBy = branding?.poweredBy ?? true;
  if (!branding?.supportUrl && !branding?.supportEmail && !poweredBy) return null;
  return (
    <footer className="mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-3 px-4 pb-8 text-xs text-fg-muted">
      <span className="flex flex-wrap gap-3">
        {branding?.supportUrl && (
          <a href={branding.supportUrl} target="_blank" rel="noreferrer noopener" className="hover:text-fg hover:underline">
            {t("public.support")}
          </a>
        )}
        {branding?.supportEmail && (
          <a href={`mailto:${branding.supportEmail}`} className="hover:text-fg hover:underline">
            {branding.supportEmail}
          </a>
        )}
      </span>
      {poweredBy && <span>{t("public.poweredBy")}</span>}
    </footer>
  );
}

function PublicShell({ children, branding }: { children: ReactNode; branding?: S.PublicBranding | null }) {
  return (
    <div className="min-h-full bg-bg" style={brandStyle(branding?.primaryColor)}>
      <header className="border-b bg-panel">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <BrandLogo branding={branding} />
          <LanguageMenu />
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6 md:py-10">{children}</main>
      <PublicFooter branding={branding} />
    </div>
  );
}

function PageState({ token, children }: { token: string; children: (page: S.PublicPage) => ReactNode }) {
  const { t } = useTranslation();
  const page = usePublicPage(token);
  useEffect(() => {
    if (page.data) document.title = `${page.data.app.name} – ${page.data.branding?.name ?? "Shipyard"}`;
  }, [page.data]);
  return (
    <PublicShell branding={page.data?.branding}>
      {page.isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner className="h-6 w-6" />
        </div>
      ) : page.error || !page.data ? (
        <EmptyState icon={<XCircle className="h-8 w-8" />} title={t("public.notFound")}>
          {t("public.notFoundHelp")}
        </EmptyState>
      ) : (
        children(page.data)
      )}
    </PublicShell>
  );
}

/** Platforms come from the published builds only: private builds of the other platform stay hidden. */
function AppHeader({ page }: { page: S.PublicPage }) {
  const { t, i18n } = useTranslation();
  const platforms = new Set(page.builds.map((b) => b.platform));
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-semibold tracking-tight">{page.app.name}</h1>
      <p className="mt-1 flex flex-wrap items-center gap-3 text-sm text-fg-muted">
        {platforms.has("ios") && (
          <span className="flex items-center gap-1">
            <PlatformIcon platform="ios" className="h-3.5 w-3.5" />
            iOS
          </span>
        )}
        {platforms.has("android") && (
          <span className="flex items-center gap-1">
            <PlatformIcon platform="android" className="h-3.5 w-3.5" />
            Android
          </span>
        )}
        {page.expiresAt && <span>{t("public.expires", { date: formatDate(page.expiresAt, i18n.language) })}</span>}
      </p>
    </div>
  );
}

/** Tester notes in the visitor's language when available. */
function Notes({ build }: { build: S.PublicBuild }) {
  const { i18n, t } = useTranslation();
  if (!build.notes.length) return null;
  const lang = i18n.language.slice(0, 2);
  const note = build.notes.find((n) => n.locale.toLowerCase().startsWith(lang)) ?? build.notes[0]!;
  return (
    <div className="mt-4">
      <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">{t("public.whatToTest")}</p>
      <p className="mt-1 whitespace-pre-line text-sm">{note.text}</p>
    </div>
  );
}

// ------------------------------------------------------------------ App page

export function PublicAppPage({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const visitor = detectVisitor();
  return (
    <PageState token={token}>
      {(page) => {
        if (page.kind === "build" && page.builds[0]) return <BuildView token={token} page={page} buildId={page.builds[0].id} />;
        // Builds for the visitor's platform first.
        const builds = [...page.builds].sort((a, b) => Number(b.platform === visitor) - Number(a.platform === visitor));
        return (
          <>
            <AppHeader page={page} />
            {!builds.length ? (
              <EmptyState title={t("public.noBuilds")} />
            ) : (
              <Card className="divide-y">
                {builds.map((b) => (
                  <Link
                    key={b.id}
                    to="/p/$token/b/$buildId"
                    params={{ token, buildId: b.id }}
                    className={cn("flex items-center gap-3 px-4 py-3 hover:bg-muted/50", visitor !== "desktop" && b.platform !== visitor && "opacity-60")}
                  >
                    <PlatformIcon platform={b.platform} className="h-5 w-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {b.version} <span className="font-normal text-fg-muted">({b.buildNumber})</span>
                      </p>
                      <p className="text-xs text-fg-muted">{formatDate(b.createdAt, i18n.language)}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-fg-muted" />
                  </Link>
                ))}
              </Card>
            )}
          </>
        );
      }}
    </PageState>
  );
}

// ------------------------------------------------------------------ Build page

export function PublicBuildPage({ token, buildId }: { token: string; buildId: string }) {
  return <PageState token={token}>{(page) => <BuildView token={token} page={page} buildId={buildId} />}</PageState>;
}

function BuildView({ token, page, buildId }: { token: string; page: S.PublicPage; buildId: string }) {
  const { t, i18n } = useTranslation();
  const build = page.builds.find((b) => b.id === buildId);
  if (!build) return <EmptyState icon={<XCircle className="h-8 w-8" />} title={t("public.buildNotFound")} />;
  return (
          <>
            {page.kind === "app" && (
              <Link to="/p/$token" params={{ token }} className="mb-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
                ← {t("public.allBuilds")}
              </Link>
            )}
            <AppHeader page={page} />
            <Card className="p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <PlatformIcon platform={build.platform} className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-lg font-semibold">
                    {build.version} <span className="font-normal text-fg-muted">({build.buildNumber})</span>
                  </p>
                  <p className="text-xs text-fg-muted">{formatDate(build.createdAt, i18n.language)}</p>
                </div>
              </div>
              <Notes build={build} />
            </Card>
            <div className="mt-6">
              <Install token={token} build={build} />
            </div>
          </>
  );
}

function Install({ token, build }: { token: string; build: S.PublicBuild }) {
  const visitor = detectVisitor();
  if (visitor === "desktop" || visitor !== build.platform) return <OpenOnPhone build={build} token={token} />;
  return build.platform === "android" ? <AndroidInstall token={token} build={build} /> : <IosInstall token={token} build={build} />;
}

function QrCode({ value }: { value: string }) {
  const [svg, setSvg] = useState("");
  useEffect(() => {
    void QRCode.toString(value, { type: "svg", margin: 1, width: 180 }).then(setSvg);
  }, [value]);
  return <div className="h-[180px] w-[180px] rounded-md bg-white p-1" dangerouslySetInnerHTML={{ __html: svg }} />;
}

function OpenOnPhone({ token, build }: { token: string; build: S.PublicBuild }) {
  const { t } = useTranslation();
  const install = usePublicInstall(token, build.id, null, build.platform === "android");
  return (
    <Card className="flex flex-col items-center gap-4 p-6 text-center sm:flex-row sm:text-left">
      <QrCode value={window.location.href.split("?")[0]!} />
      <div className="space-y-2">
        <p className="flex items-center justify-center gap-2 font-medium sm:justify-start">
          <Smartphone className="h-4 w-4" />
          {t(build.platform === "ios" ? "public.openOnIphone" : "public.openOnAndroid")}
        </p>
        <p className="text-sm text-fg-muted">{t("public.scanHelp")}</p>
        {build.platform === "android" && install.data?.status === "ready" && install.data.installUrl && (
          <a href={install.data.installUrl} className="inline-block pt-1 text-sm font-medium text-brand-600 hover:underline">
            {t("public.downloadApk")}
          </a>
        )}
      </div>
    </Card>
  );
}

function StatusCard({ icon, title, children, tone = "neutral" }: { icon: ReactNode; title: ReactNode; children?: ReactNode; tone?: "neutral" | "error" | "ok" }) {
  return (
    <Card className="p-5">
      <div className="flex gap-3">
        <span className={cn("mt-0.5 shrink-0", tone === "error" ? "text-red-600" : tone === "ok" ? "text-emerald-600" : "text-fg-muted")}>{icon}</span>
        <div className="min-w-0 flex-1 space-y-3">
          <p className="font-medium">{title}</p>
          {children}
        </div>
      </div>
    </Card>
  );
}

function CommonStates({ install }: { install: S.PublicInstall }) {
  const { t } = useTranslation();
  if (install.status === "preparing") return <StatusCard icon={<Spinner className="h-5 w-5" />} title={t("public.preparing")}>{<p className="text-sm text-fg-muted">{t("public.preparingHelp")}</p>}</StatusCard>;
  if (install.status === "failed" || install.status === "unavailable")
    return (
      <StatusCard icon={<AlertTriangle className="h-5 w-5" />} tone="error" title={t(install.status === "failed" ? "public.failed" : "public.unavailable")}>
        {install.message && <p className="text-sm text-fg-muted">{install.message}</p>}
      </StatusCard>
    );
  return null;
}

function AndroidInstall({ token, build }: { token: string; build: S.PublicBuild }) {
  const { t } = useTranslation();
  const install = usePublicInstall(token, build.id, null);
  if (install.isLoading) return <Spinner />;
  if (!install.data) return null;
  if (install.data.status !== "ready") return <CommonStates install={install.data} />;
  return (
    <StatusCard icon={<CheckCircle2 className="h-5 w-5" />} tone="ok" title={t("public.apkReady")}>
      <a href={install.data.installUrl!}>
        <Button className="w-full justify-center sm:w-auto">
          <Download className="h-4 w-4" />
          {t("public.downloadApk")}
        </Button>
      </a>
      <p className="text-xs text-fg-muted">{t("public.apkHelp")}</p>
    </StatusCard>
  );
}

function IosInstall({ token, build }: { token: string; build: S.PublicBuild }) {
  const { t } = useTranslation();
  const device = useDeviceId();
  const install = usePublicInstall(token, build.id, device);
  const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
  if (install.isLoading) return <Spinner />;
  if (!install.data) return null;
  const data = install.data;
  const enroll = `/api/v1/public/${token}/enroll.mobileconfig?build=${build.id}`;

  const safariWarning = !isSafari && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">{t("public.useSafari")}</p>;

  if (data.status === "waiting_device")
    return (
      <StatusCard icon={<Smartphone className="h-5 w-5" />} title={t("public.registerTitle")}>
        <p className="text-sm text-fg-muted">{t("public.registerHelp")}</p>
        {safariWarning}
        <a href={enroll}>
          <Button className="w-full justify-center sm:w-auto">{t("public.registerButton")}</Button>
        </a>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-fg-muted">
          <li>{t("public.registerStep1")}</li>
          <li>{t("public.registerStep2")}</li>
          <li>{t("public.registerStep3")}</li>
        </ol>
      </StatusCard>
    );
  if (data.status === "pending_approval")
    return (
      <StatusCard icon={<Clock className="h-5 w-5" />} title={t("public.pendingTitle", { name: data.device?.name })}>
        <p className="text-sm text-fg-muted">{t("public.pendingHelp")}</p>
      </StatusCard>
    );
  if (data.status === "rejected")
    return <StatusCard icon={<XCircle className="h-5 w-5" />} tone="error" title={t("public.rejected")} />;
  if (data.status !== "ready") return <CommonStates install={data} />;
  return (
    <StatusCard icon={<CheckCircle2 className="h-5 w-5" />} tone="ok" title={t("public.iosReady", { name: data.device?.name })}>
      {safariWarning}
      <a href={data.installUrl!}>
        <Button className="w-full justify-center sm:w-auto">
          <Download className="h-4 w-4" />
          {t("public.install")}
        </Button>
      </a>
      <p className="text-xs text-fg-muted">{t("public.installHelp")}</p>
    </StatusCard>
  );
}
